import { type ReactNode, useState } from "react";
import {
  ArrowUpRightIcon,
  CalendarIcon,
  CalendarPlusIcon,
  ClockIcon,
  LoaderCircleIcon,
  Trash2Icon,
} from "lucide-react";

import { DashboardTaskOverdueBadge } from "./DashboardTaskOverdueBadge";

import { ConfirmationDialog } from "@/components/ConfirmationDialog";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Avatar } from "@/components/quick-access/Avatar";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useDeleteRemoveDashboardTask } from "@/hooks/deleteRemoves/useDeleteRemoveDashboardTask";
import { useUpdateSetDashboardTask } from "@/hooks/updateSets/useUpdateSetDashboardTask";
import { useUpdateSetPlacementStartDate } from "@/hooks/updateSets/useUpdateSetPlacementStartDate";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { getCandidacyLink } from "@/lib/notificationLinks";
import { cn } from "@/lib/utils";
import { DashboardTask, TaskType } from "@/types";

export const DashboardTaskRow = ({
  task,
  day,
  variant = "compact",
  showOwnerAvatar = false,
}: {
  task: DashboardTask;
  day: Date;
  variant?: "compact" | "comfortable";
  showOwnerAvatar?: boolean;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateTask } = useUpdateSetDashboardTask();
  // Delete
  const { mutate: deleteTask, isPending: isDeleting } =
    useDeleteRemoveDashboardTask();

  /* React Hooks */
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  /* Helpers */
  const isComfortable = variant === "comfortable";
  const isCompleted = task.completedDate != null;
  const isMissingStart = task.type === TaskType.MISSING_START_DATE;
  const isConfirmStart = task.type === TaskType.PLACEMENT_DAY_OF;
  // General = untyped, user-created tasks; only these can be deleted.
  const isGeneral = task.type == null;

  const candidacyHref =
    task.companyId && task.jobId && task.candidacyId
      ? getCandidacyLink({
          candidacyId: task.candidacyId,
          companyId: task.companyId,
          jobId: task.jobId,
        })
      : null;

  const gap = isComfortable ? "gap-2.5" : "gap-1.5";
  const checkboxClassName = cn(
    "shrink-0 rounded-full",
    // Compact box is smaller than the checkbox's fixed 16px check icon —
    // scale the icon down so it stays centered.
    isComfortable ? "h-4 w-4" : "h-3.5 w-3.5 [&_svg]:h-3 [&_svg]:w-3",
  );

  /* Handlers */
  const handleToggle = (checked: boolean) => {
    mixpanelUtils.trackButtonClick(
      checked ? "Complete task" : "Reopen task",
      MIXPANEL_SCREENS.DASHBOARD,
      { task_id: task.id },
    );
    updateTask({
      id: task.id,
      completedDate: checked ? new Date().toISOString() : null,
    });
  };

  const handleOpen = () => {
    mixpanelUtils.trackButtonClick(
      "Open task candidate",
      MIXPANEL_SCREENS.DASHBOARD,
      {
        task_id: task.id,
      },
    );
  };

  const handleDelete = () => {
    mixpanelUtils.trackButtonClick("Delete task", MIXPANEL_SCREENS.DASHBOARD, {
      task_id: task.id,
    });
    deleteTask(task.id, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
      },
    });
  };

  /* Render */
  // Missing-start and confirm-start tasks are resolved by setting a date, not by
  // checking off: an unresolved one shows the date setter, a resolved one a
  // static checkmark.
  const renderLeading = () => {
    if (isMissingStart || isConfirmStart) {
      if (isCompleted) {
        return <Checkbox checked className={checkboxClassName} />;
      }
      return (
        <MissingStartDateSetter
          task={task}
          buttonClassName={checkboxClassName}
        />
      );
    }
    return (
      <Checkbox
        checked={isCompleted}
        className={checkboxClassName}
        onCheckedChange={(checked) => {
          handleToggle(checked === true);
        }}
      />
    );
  };

  const render = () => (
    <TooltipProvider delayDuration={500}>
      <Row
        className={cn(
          "group items-center rounded-lg",
          gap,
          isComfortable ? "px-2 py-2" : "px-1.5 py-0.5",
          candidacyHref && "group-hover:bg-slate-50",
        )}
      >
        {renderLeading()}
        {candidacyHref ? (
          <a
            className={cn("flex min-w-0 flex-1 items-center", gap)}
            href={candidacyHref}
            onClick={handleOpen}
            rel="noopener noreferrer"
            target="_blank"
          >
            <DashboardTaskItem
              isClickable
              day={day}
              showOwnerAvatar={showOwnerAvatar}
              task={task}
              variant={variant}
            />
          </a>
        ) : (
          <DashboardTaskItem
            isClickable={false}
            day={day}
            showOwnerAvatar={showOwnerAvatar}
            task={task}
            variant={variant}
          />
        )}
        {isGeneral ? (
          <Button
            // Sized to match the checkbox so revealing it on hover never grows the row.
            className={cn(
              "hidden shrink-0 p-0 text-slate-400 hover:bg-red-50 hover:text-red-600 group-hover:flex",
              isComfortable ? "h-4 w-4" : "h-3.5 w-3.5 [&_svg]:h-3 [&_svg]:w-3",
            )}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
            size="icon"
            type="button"
            variant="ghost"
          >
            <Trash2Icon className="pointer-events-none" />
          </Button>
        ) : null}
        {/* Status badge sits far right, after the action icons. */}
        {renderTaskBadge(task, isComfortable)}
        {isGeneral ? (
          <ConfirmationDialog
            confirmButtonText="Delete"
            confirmationButtonVariant="destructive"
            description={`Delete "${task.title ?? "this task"}"? This can't be undone.`}
            isLoading={isDeleting}
            onConfirm={handleDelete}
            onOpenChange={setConfirmDeleteOpen}
            open={confirmDeleteOpen}
            title="Delete task"
          />
        ) : null}
      </Row>
    </TooltipProvider>
  );

  return render();
};

// The task's linkable content: owner avatar, title, and status badge, plus the
// on-hover treatment (blue title + revealed open arrow) when it's clickable.
const DashboardTaskItem = ({
  task,
  day,
  variant,
  showOwnerAvatar,
  isClickable,
}: {
  task: DashboardTask;
  day: Date;
  variant: "compact" | "comfortable";
  showOwnerAvatar: boolean;
  isClickable: boolean;
}) => {
  /* React Hooks */
  const { userInitialsMap } = useTaggableUsers();

  /* Helpers */
  const isComfortable = variant === "comfortable";
  // General (untyped) and missing-start tasks can have their due date changed inline.
  const isGeneral = task.type == null;
  const isMissingStart = task.type === TaskType.MISSING_START_DATE;
  const canEditDueDate = isGeneral || isMissingStart;
  // Single ownership on the FE — read the first assignee only.
  const ownerUserId =
    task.assigneeUserIds.length > 0 ? task.assigneeUserIds[0] : null;
  const isCompleted = task.completedDate != null;

  /* Render */

  const render = () => (
    <>
      {showOwnerAvatar && ownerUserId != null ? (
        <Avatar
          className="shrink-0"
          initials={userInitialsMap[ownerUserId] || "?"}
        />
      ) : null}
      <Text
        truncate
        variant="secondary"
        className={cn(
          "min-w-0 flex-1 text-left leading-snug",
          isComfortable ? "text-sm" : "text-[11px]",
          isCompleted && "text-slate-400 line-through",
          isClickable && "group-hover:text-blue-600",
        )}
      >
        {task.title}
      </Text>
      {canEditDueDate ? (
        <TaskDueDateEditor
          // Sized to match the checkbox so revealing it on hover never grows the row.
          iconSizeClassName={
            isComfortable ? "h-4 w-4" : "h-3.5 w-3.5 [&_svg]:h-3 [&_svg]:w-3"
          }
          task={task}
          viewedDay={day}
        />
      ) : null}
      {isClickable ? (
        <ArrowUpRightIcon
          className={cn(
            "hidden shrink-0 text-blue-600 group-hover:block",
            isComfortable ? "h-4 w-4" : "h-3.5 w-3.5",
          )}
        />
      ) : null}
    </>
  );

  return render();
};

// Calendar icon (revealed on hover) that re-dates a task's due date; the picker
// opens on the day currently in view.
const TaskDueDateEditor = ({
  task,
  viewedDay,
  iconSizeClassName,
}: {
  task: DashboardTask;
  viewedDay: Date;
  iconSizeClassName: string;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateTask } = useUpdateSetDashboardTask();

  /* React Hooks */
  const [open, setOpen] = useState(false);

  /* Handlers */
  const handleSelect = (date: Date | undefined) => {
    if (!date) return;
    mixpanelUtils.trackButtonClick(
      "Change task due date",
      MIXPANEL_SCREENS.DASHBOARD,
      { task_id: task.id },
    );
    updateTask({
      id: task.id,
      dueDate: dates.getStartOfLocalDay(date).toISOString(),
    });
    setOpen(false);
  };

  /* Render */
  const render = () => (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip delayDuration={500}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              // Hidden until row hover, but pinned visible while the picker is open.
              className={cn(
                "shrink-0 p-0 text-slate-400 hover:bg-slate-100 hover:text-slate-600",
                iconSizeClassName,
                open ? "block" : "hidden group-hover:block",
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen((prev) => !prev);
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <CalendarIcon className="pointer-events-none" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent className="text-xs">Reschedule reminder</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-auto p-0">
        <Calendar mode="single" onSelect={handleSelect} selected={viewedDay} />
      </PopoverContent>
    </Popover>
  );

  return render();
};

// Calendar-plus that opens a date picker (pre-selected to the placement's
// planned start, if one exists) to confirm its actual start date. The backend
// cascades this: completes the linked missing-start/confirm-start task, spins
// up check-ins, and promotes the candidacy to Employed.
const MissingStartDateSetter = ({
  task,
  buttonClassName,
}: {
  task: DashboardTask;
  buttonClassName: string;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: setStartDate, isPending } = useUpdateSetPlacementStartDate();

  /* React Hooks */
  const [open, setOpen] = useState(false);

  /* Helpers */
  const { companyId, jobId, linkedEntityId } = task;
  const canSet = companyId != null && jobId != null && linkedEntityId != null;
  const defaultSelected = task.linkedStartDate
    ? new Date(task.linkedStartDate)
    : undefined;

  /* Handlers */
  const handleSelect = (date: Date | undefined) => {
    if (!date || companyId == null || jobId == null || linkedEntityId == null) {
      return;
    }
    mixpanelUtils.trackButtonClick(
      "Confirm placement start date",
      MIXPANEL_SCREENS.DASHBOARD,
      { task_id: task.id },
    );
    setStartDate({
      companyId,
      jobId,
      id: linkedEntityId,
      dateStarted: date.toISOString(),
    });
    setOpen(false);
  };

  /* Render */
  const render = () => (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip delayDuration={500}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              className={cn(
                // Match the missing-start category theme (red), not a generic action blue.
                "p-0 text-red-600 hover:bg-red-50 hover:text-red-700",
                buttonClassName,
              )}
              disabled={isPending || !canSet}
              size="icon"
              type="button"
              variant="ghost"
            >
              {isPending ? (
                <LoaderCircleIcon className="pointer-events-none h-3.5 w-3.5 animate-spin" />
              ) : (
                <CalendarPlusIcon className="pointer-events-none h-3.5 w-3.5" />
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent className="text-xs">Confirm start date</TooltipContent>
      </Tooltip>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          defaultMonth={defaultSelected}
          mode="single"
          onSelect={handleSelect}
          selected={defaultSelected}
        />
      </PopoverContent>
    </Popover>
  );

  return render();
};

// The row's rightmost status badge: a grey resolved-start-date pill or the red
// overdue pill. Compact reveals the pill on hover behind an icon; comfortable
// shows it inline.
const renderTaskBadge = (task: DashboardTask, isComfortable: boolean) => {
  const isCompleted = task.completedDate != null;
  const isMissingStart = task.type === TaskType.MISSING_START_DATE;
  const isOverdue =
    !isCompleted &&
    task.dueDate != null &&
    dates.taskDueDate(task.dueDate) < dates.getStartOfLocalDay(new Date());

  const withHover = (icon: ReactNode, content: ReactNode) => {
    if (isComfortable) return content;
    return (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>{icon}</TooltipTrigger>
        <TooltipContent
          align="end"
          className="border-none bg-transparent p-0 shadow-none"
          side="top"
        >
          {content}
        </TooltipContent>
      </Tooltip>
    );
  };

  // Resolved missing-start: grey badge with the start date that was set.
  if (isMissingStart && isCompleted && task.linkedStartDate != null) {
    return withHover(
      <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />,
      <DashboardTaskStartDateBadge startDate={task.linkedStartDate} />,
    );
  }
  if (!isOverdue || task.dueDate == null) return null;
  return withHover(
    <ClockIcon className="h-3.5 w-3.5 shrink-0 text-red-500" />,
    <DashboardTaskOverdueBadge dueDate={task.dueDate} />,
  );
};

// Grey counterpart to the overdue badge — the start date a missing-start task
// was resolved with.
const DashboardTaskStartDateBadge = ({ startDate }: { startDate: string }) => {
  const render = () => (
    <Row className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5">
      <CalendarIcon className="h-3 w-3 shrink-0 text-slate-400" />
      <Text className="text-[11px] font-medium text-slate-500">
        {dates.formatShortDate(startDate)}
      </Text>
    </Row>
  );

  return render();
};
